import { useQuery } from "@tanstack/react-query";
import {
  ApiException,
  GetOrderReturnEligibilityBatchRequest,
  OrdersClient,
  OrdersViewType,
  ProblemDetails,
  SearchOrderRequestFilter,
  SearchOrdersRequest,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";
import { useClaims } from "../../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../../auth/authorizationTypes";
import type { OrderDrawerIdentifier, OrderDrawerPreloadedData } from "./types";
import { OrderRow } from "@features/orders-features/types/types";
import { OrderReturnEligibilityQueryKeys } from "@features/ecommerce/hooks/useGetOrderReturnEligibility";
import ms from "ms";
import {
  getInvoiceStatus,
  getShipTo,
  hasInvoice,
  isOrderLineItemViewArray,
} from "@features/orders-features/functions/mapToOrderRowHelpers";
import { toOrderLines } from "@features/orders-features/functions/mapToOrderRows";
import { resolveItProjectOrderStatusValue } from "shared-ui/src/features/it-projects-feature";

interface UseOrderDrawerDataOptions {
  identifier: OrderDrawerIdentifier | null;
  preloadedData: OrderDrawerPreloadedData | null;
  enabled: boolean;
}

interface OrderDrawerDataResult {
  /** Whether live data is still being fetched */
  isLoading: boolean;
  /** Whether live fetch failed */
  isError: boolean;
  /** Whether we have enough data to render (preloaded or fetched) */
  hasData: boolean;
  /** The order row data (either from preloaded or live fetch) */
  orderData: OrderRow | null;
}

/**
 * Hook that determines whether the drawer has enough preloaded data
 * or needs to fetch live from the API.
 *
 * Strategy:
 * - If preloadedData contains lineItems/orderLineProducts, use it directly.
 * - Otherwise, fetch from the search API using the salesOrder number.
 */
export function useOrderDrawerData({
  identifier,
  preloadedData,
  enabled,
}: UseOrderDrawerDataOptions): OrderDrawerDataResult {
  const api = useApi(OrdersClient);
  const { hasPermission, getClaimValue } = useClaims();

  const canSearchOrders =
    hasPermission(ShiOnePermission.orderSearchMy) ||
    hasPermission(ShiOnePermission.orderSearchOrgUnit);

  const hasOrgUnitSearch = hasPermission(ShiOnePermission.orderSearchOrgUnit);

  const hasRmaRequest = hasPermission(ShiOnePermission.orderRmaRequest);

  const orgUnitIdCurrent = Number(getClaimValue("orgUnitIdCurrent") ?? 0);

  const resolvedOrgUnitId = Number(identifier?.orgUnitId || orgUnitIdCurrent);

  // Determine if preloaded data has line items (full detail)
  const hasPreloadedLineItems =
    preloadedData?.lineItems != null &&
    isOrderLineItemViewArray(preloadedData.lineItems) &&
    preloadedData.lineItems.length > 0;
  const hasPreloadedProducts =
    preloadedData?.orderLineProducts != null &&
    Object.keys(preloadedData.orderLineProducts).length > 0;
  const hasFullPreloadedData = hasPreloadedLineItems || hasPreloadedProducts;

  // Preloaded data exists at all (even without line items — enough for summary/payment)
  const hasAnyPreloadedData =
    preloadedData != null &&
    (preloadedData.status != null ||
      preloadedData.total != null ||
      preloadedData.purchaseOrder != null);

  const hasValidBiOrderId = (identifier?.biOrderId ?? 0) > 0;
  const hasValidWebOrderId = (identifier?.webOrderId ?? 0) > 0;
  const salesOrder = identifier?.salesOrder?.trim();
  const hasValidSalesOrder = !!salesOrder;

  // Orders opened with a bi/web order id come from order-search surfaces and rely on the
  // order-search permission. A sales-order-only lookup (e.g. opened from Purchased Assets)
  // instead uses the account-scoped by-sales-order endpoint, which is gated server-side by
  // asset visibility -- so it must not require the order-search permission here.
  const canFetchByIds =
    canSearchOrders && (hasValidBiOrderId || hasValidWebOrderId);
  const canFetchBySalesOrder =
    hasValidSalesOrder && !hasValidBiOrderId && !hasValidWebOrderId;

  // Fetch to enrich when we don't have full detail (line items).
  const needsLiveFetch =
    enabled &&
    !hasFullPreloadedData &&
    (canFetchByIds || canFetchBySalesOrder);

  const query = useQuery({
    queryKey: [
      "order-drawer-detail",
      resolvedOrgUnitId,
      hasValidBiOrderId
        ? `bi-${identifier?.biOrderId}`
        : hasValidWebOrderId
          ? `web-${identifier?.webOrderId}`
          : `so-${salesOrder}`,
    ],
    queryFn: async () => {
      // Sales-order-only lookups (e.g. opened from Purchased Assets) use the account-scoped
      // by-sales-order endpoint so an order placed by another user on the account still
      // resolves, without widening order-search visibility.
      if (canFetchBySalesOrder) {
        try {
          const order = await api.getOrderBySalesOrderNumber(salesOrder!);
          return order ?? null;
        } catch (e) {
          // 404 means no order is visible to this user for that sales order (e.g. outside
          // their account scope) -- surface the empty state, not a false error. The generated
          // client throws the parsed ProblemDetails body for a declared 404.
          if (
            (e instanceof ProblemDetails || e instanceof ApiException) &&
            e.status === 404
          ) {
            return null;
          }
          throw e;
        }
      }

      if (!hasValidBiOrderId && !hasValidWebOrderId) {
        return null;
      }

      const searchFilterInclude = hasValidBiOrderId
        ? new SearchOrderRequestFilter({ biOrderIds: [identifier!.biOrderId!] })
        : new SearchOrderRequestFilter({
            webOrderIds: [identifier!.webOrderId!],
          });

      const request = new SearchOrdersRequest({
        ordersViewType: hasOrgUnitSearch
          ? OrdersViewType.All
          : OrdersViewType.My,
        orgUnitId: resolvedOrgUnitId,
        page: 1,
        pageSize: 1,
        searchFilterInclude,
      });

      const result = await api.searchOrders(request);

      const items = result.items ?? [];

      // Return null (never undefined) when no match is found. React Query treats an
      // undefined queryFn result as an error, which would surface a false
      // "Unable to load order details" message instead of an empty state.
      if (hasValidBiOrderId) {
        return (
          items.find((item) => item.biOrderId === identifier!.biOrderId!) ??
          null
        );
      }
      return (
        items.find((item) => item.webOrderId === identifier!.webOrderId!) ??
        null
      );
    },
    enabled: needsLiveFetch,
    retry: 1,
    staleTime: ms("5m"),
  });

  const resolvedBiOrderId = query.data?.biOrderId ?? identifier?.biOrderId;

  // Return settings are per storefront. A cold link has no orgUnitId, so wait for the
  // fetched order's storefront instead of checking the current one.
  // React Query v4: isLoading means no data yet, including a fetch paused offline.
  const isLookupPending = needsLiveFetch && query.isLoading;
  const eligibilityOrgUnitId = Number(
    query.data?.orgUnitId ||
      identifier?.orgUnitId ||
      (isLookupPending ? 0 : orgUnitIdCurrent),
  );

  const returnEligibility = useQuery({
    queryKey: [
      OrderReturnEligibilityQueryKeys.ReturnEligibility,
      resolvedBiOrderId,
      eligibilityOrgUnitId,
    ],
    queryFn: async () => {
      if (resolvedBiOrderId == null) return null;

      const request = new GetOrderReturnEligibilityBatchRequest({
        biOrderIds: [resolvedBiOrderId],
        orgUnitId: eligibilityOrgUnitId,
      });

      const result = await api.getReturnOrderEligibilityBatch(request);
      const res = result?.[0];
      if (!res || res.biOrderId !== resolvedBiOrderId) {
        return null;
      }
      return res;
    },
    enabled: hasRmaRequest && !!resolvedBiOrderId && eligibilityOrgUnitId > 0,
    retry: false,
    staleTime: ms("5m"),
  });

  // Build unified OrderRow from either source
  if (hasFullPreloadedData && identifier) {
    const orderRow: OrderRow = {
      id: `drawer-${identifier.salesOrder}`,
      orgUnitId: identifier.orgUnitId,
      crmAccountId: identifier.crmAccountId,
      salesOrder: identifier.salesOrder,
      orderDate: preloadedData?.orderDate ?? "",
      status: preloadedData?.status ?? "",
      purchaseOrder: preloadedData?.purchaseOrder ?? "",
      orderedBy: preloadedData?.orderedBy ?? "",
      shipTo: preloadedData?.shipTo ?? {},
      totalItems: preloadedData?.totalItems ?? 0,
      total: preloadedData?.total ?? 0,
      currencyCode: preloadedData?.currencyCode ?? null,
      invoiceStatus: preloadedData?.invoiceStatus ?? "",
      biOrderId: identifier.biOrderId,
      webOrderId: identifier.webOrderId,
      orderLineProducts: preloadedData?.orderLineProducts,
      allLineItems: preloadedData?.lineItems,
      canReturn: preloadedData?.canReturn,
      hasInvoice: preloadedData?.invoiceStatus?.toLowerCase() === "invoiced",
    };

    return {
      isLoading: false,
      isError: false,
      hasData: true,
      orderData: orderRow,
    };
  }

  if (query.data && identifier && !returnEligibility.isFetching) {
    const order = query.data;
    const { orderLineProducts, allLineItems } = toOrderLines(order);
    const orderRow: OrderRow = {
      id: order.id ?? `drawer-${identifier.salesOrder}`,
      orgUnitId: order.orgUnitId,
      crmAccountId: order.crmAccountId ?? identifier.crmAccountId,
      salesOrder:
        order.salesOrderNumber ?? order.orderNumber ?? identifier.salesOrder,
      orderDate: order.orderDate ? order.orderDate.toISOString() : "",
      // Mirror the View Orders page resolution (fulfillmentStatus + fulfillment groups)
      // so the drawer status matches the grid for partial/composite orders.
      status:
        resolveItProjectOrderStatusValue({
          fulfillmentStatus: order.fulfillmentStatus,
          status: order.status,
          fulfillmentGroups: order.fulfillmentGroups,
        }) ?? "",
      purchaseOrder: order.customerPoNumber ?? order.purchaseOrder ?? "",
      orderedBy: order.orderedBy ?? "",
      shipTo: getShipTo(order),
      totalItems: order.totalItems ?? 0,
      total: order.total ?? 0,
      currencyCode: order.currencyAbbr ?? null,
      invoiceStatus: getInvoiceStatus(order),
      biOrderId: order.biOrderId ?? identifier.biOrderId,
      webOrderId: order.webOrderId ?? identifier.webOrderId,
      orderLineProducts,
      allLineItems,
      canReturn: returnEligibility.data?.canReturn,
      hasInvoice: hasInvoice(order),
    };

    return {
      isLoading: false,
      isError: false,
      hasData: true,
      orderData: orderRow,
    };
  }

  // Fallback: use partial preloaded data while search is loading or if it failed
  if (hasAnyPreloadedData && identifier) {
    const orderRow: OrderRow = {
      id: `drawer-${identifier.salesOrder}`,
      orgUnitId: identifier.orgUnitId,
      crmAccountId: identifier.crmAccountId,
      salesOrder: identifier.salesOrder,
      orderDate: preloadedData?.orderDate ?? "",
      status: preloadedData?.status ?? "",
      purchaseOrder: preloadedData?.purchaseOrder ?? "",
      orderedBy: preloadedData?.orderedBy ?? "",
      shipTo: preloadedData?.shipTo ?? {},
      totalItems: preloadedData?.totalItems ?? 0,
      total: preloadedData?.total ?? 0,
      currencyCode: preloadedData?.currencyCode ?? null,
      invoiceStatus: preloadedData?.invoiceStatus ?? "",
      biOrderId: identifier.biOrderId,
      webOrderId: identifier.webOrderId,
      canReturn: preloadedData?.canReturn,
      hasInvoice: preloadedData?.invoiceStatus?.toLowerCase() === "invoiced",
    };

    return {
      isLoading: query.isFetching,
      isError: false,
      hasData: true,
      orderData: orderRow,
    };
  }

  return {
    // A lookup paused offline is pending but not fetching; it is not a missing order.
    isLoading:
      isLookupPending || query.isFetching || returnEligibility.isFetching,
    isError: query.isError,
    hasData: false,
    orderData: null,
  };
}
